import { z } from 'zod';
import {
  ActorKindSchema,
  AgentRunStatusSchema,
  AssessmentStateSchema,
  ExecutionTypeSchema,
} from './enums';
import {
  AssessmentIdSchema,
  AuditIdSchema,
  PackBindingSchema,
  RunIdSchema,
  TenantIdSchema,
} from './ids';
import { DetailBagSchema, NonEmptyStringSchema, Sha256Schema, TimestampSchema } from './primitives';

/**
 * Who did something.
 *
 * DESIGN_SPEC §7.9 models the actor as a bare string. It is an object here
 * because the distinction between kinds is enforced, not decorative: the guard
 * on `approved_for_reporting` requires `kind === 'user'`, which is the
 * mechanism that stops any worker from walking through the human gate
 * (docs/DECISIONS.md ZD-007).
 */
export const AuditActorSchema = z.object({
  kind: ActorKindSchema,
  /** A user_id, a worker_name, or `orchestrator` / `system`. */
  id: NonEmptyStringSchema,
});
export type AuditActor = z.infer<typeof AuditActorSchema>;

/**
 * Audit event (DESIGN_SPEC §7.9, §15). Every state transition and every worker
 * run writes one. Append-only: audit events are never updated or deleted.
 */
export const AuditEventSchema = z.object({
  audit_id: AuditIdSchema,
  tenant_id: TenantIdSchema,
  /** Null for tenant-level events that belong to no single assessment. */
  assessment_id: AssessmentIdSchema.nullable(),
  actor: AuditActorSchema,
  action: NonEmptyStringSchema,
  from_state: AssessmentStateSchema.nullable(),
  to_state: AssessmentStateSchema.nullable(),
  at: TimestampSchema,
  detail: DetailBagSchema,
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;
/**
 * Agent run (DESIGN_SPEC §7.9, §12.2).
 *
 * **Written by the orchestrator core and nothing else** (docs/DECISIONS.md
 * ZD-122). A stage's step hands the core a `RunSubmission` — everything below
 * except what only the core can know — and the core stamps the rest when it
 * records the transition: `status` is *derived* from whether the state machine
 * accepted the run, never asserted by the worker or the step (ZD-097's rule
 * made structural, ZD-121 §5.5). `ok` is reachable only that way. A run that
 * recorded no transition — a halt, or a throw — arrives through a method that
 * refuses `ok` by its type.
 *
 * ## The two hashes are a record, not a comparator
 *
 * `input_hash` is a digest of the facts the run was started against and
 * `output_hash` a digest of the facts patch it recorded — what went in and what
 * came out, in the orchestrator's own terms. **Two runs of one assessment never
 * hash equal**, because the patch carries the run's own minted id, so nothing
 * may read these as a drift detector. The reproducibility metric
 * (`reproducibility.ts`) compares findings by their stable keys and reads
 * neither hash; DESIGN_SPEC §12.2's *"replay and drift detection"* is what a
 * replay tool would need these for, and no such tool exists. Said here so a
 * later session does not build one on a digest that cannot agree with itself.
 *
 * `output_hash` is **null** for a run that ended in an error: it produced
 * nothing, and every alternative — a digest of the empty string, of the error
 * message, of the input again — is a manufactured value wearing a real one's
 * shape (CLAUDE.md, *check a new schema field in both directions*). A halted
 * run has an output, the account of why it halted, and hashes it.
 */
export const AgentRunSchema = z.object({
  run_id: RunIdSchema,
  tenant_id: TenantIdSchema,
  assessment_id: AssessmentIdSchema,
  worker_name: NonEmptyStringSchema,
  execution_type: ExecutionTypeSchema,

  /* Null for deterministic workers, which is most of the pipeline. */
  model: z.string().nullable(),
  temperature: z.number().nullable(),

  /**
   * Pack versions in force for this run — the other half of reproducibility.
   * Empty for a worker that reads no pack (ingestion, normalisation, the QA
   * review), which is an established emptiness and not an unrecorded one: what
   * a worker reads is a fact about the code, not about the client's documents
   * (ZD-036's rule is about extraction).
   */
  pack_versions: z.array(PackBindingSchema),

  input_hash: Sha256Schema,
  output_hash: Sha256Schema.nullable(),
  status: AgentRunStatusSchema,

  /**
   * Populated whenever status is not `ok`: the guard's reason on a `rejected`
   * run, the worker's own sentence on a halt (recorded as `rejected` — its
   * output was not one the orchestrator could accept, and it said why), the
   * error's message on an `error` run.
   *
   * **This said "whenever status is `rejected`" until ZD-122**, and the router
   * had been writing it on `error` since `sds-04n` (`llm/router.ts`,
   * `status: … ? 'rejected' : 'error'`, reason on both). The code was right and
   * the doc was the stale one; widened to match, and the name kept, because
   * renaming a column in `0001_init` to fit a doc is a contract migration on a
   * table with no rows.
   */
  rejection_reason: z.string().nullable(),

  started_at: TimestampSchema,
  ended_at: TimestampSchema,
});
export type AgentRun = z.infer<typeof AgentRunSchema>;

/**
 * What a stage's step hands the orchestrator core to record a run
 * (docs/DECISIONS.md ZD-122).
 *
 * Everything the step knows and the core does not: which worker, under which
 * model and packs, over what input, producing what, starting when. The five
 * fields omitted are the five the core stamps — `tenant_id` and
 * `assessment_id` from the header, `status` and `rejection_reason` from what
 * the state machine answered, `ended_at` from its clock at the moment of
 * recording. A step cannot therefore attribute a run to another assessment,
 * call a refused run `ok`, or backdate its end.
 */
export const RunSubmissionSchema = AgentRunSchema.pick({
  run_id: true,
  worker_name: true,
  execution_type: true,
  model: true,
  temperature: true,
  pack_versions: true,
  input_hash: true,
  output_hash: true,
  started_at: true,
});
export type RunSubmission = z.infer<typeof RunSubmissionSchema>;
